import { setNotification } from "features/Notification/notificationSlice";
import { useRef, useState } from "react";
import { useDispatch } from "react-redux";
import authHeader from "services/auth-header";
import API from "services/axios";

/**
 * Nova Shipping's "Copy to Shipments" for the whole list (user, 2026-10-22:
 * "same as bulk update system"). Every invoice, whatever its date, whose
 * tracking number is not in Shipments yet: copied (one PK-UPS / PK-DHL row, linked
 * to the customer through a work order) when the number is one customer's,
 * or under Krown's C-377 when it is a consolidated box (user, 2026-11-03);
 * no-customer / air-cargo invoices are counted and left -- they
 * are looked at again next time. Already-copied numbers are excluded.
 *
 * Asked for in chunks (POST /bundle-sync/shipments-copy-all,
 * `nextAfterInvoice`), like Bulk Update Status: progress shows as it goes,
 * and Stop ends it after the chunk in flight.
 */
export default function CopyAllToShipments({ onDone }) {
  const dispatch = useDispatch();
  const [progress, setProgress] = useState(null); // { done, total } while running
  const stopRef = useRef(false);

  const run = async () => {
    stopRef.current = false;
    const totals = { copied: 0, consolidated: 0, noCustomer: 0, notParcel: 0, noCourier: 0 };
    const missingCouriers = new Set();
    let afterInvoice = null;
    let total = 0;
    let done = 0;
    let stopped = false;
    setProgress({ done: 0, total: 0 });
    try {
      do {
        const { data } = await API.post(
          "/bundle-sync/shipments-copy-all",
          afterInvoice ? { afterInvoice } : {},
          { headers: authHeader() },
        );
        if (!data?.success) throw new Error(data?.message || "Copy failed");
        if (data.total !== undefined) total = data.total;
        done += data.processed || 0;
        for (const k of Object.keys(totals)) totals[k] += data[k] || 0;
        (data.missingCouriers || []).forEach((c) => missingCouriers.add(c));
        afterInvoice = data.nextAfterInvoice;
        setProgress({ done, total });
        if (stopRef.current && afterInvoice) stopped = true;
      } while (afterInvoice && !stopped);

      // Counts only (user, 2026-10-22: "don't show invoices in success
      // message. just count").
      const left = (n, label) => (n ? `, ${n} ${label}` : "");
      dispatch(
        setNotification({
          message:
            `${stopped ? "Stopped. " : ""}Copied ${totals.copied} to Shipments` +
            left(totals.consolidated, "consolidated (customer C-377 missing)") +
            left(totals.noCustomer, "with no customer linked") +
            left(totals.notParcel, "air cargo / not a parcel number") +
            left(
              totals.noCourier,
              `waiting for the ${[...missingCouriers].join(" / ")} courier (add it in Settings → Couriers)`,
            ),
          type: totals.noCourier ? "error" : "success",
        }),
      );
    } catch (err) {
      dispatch(
        setNotification({
          message: err?.response?.data?.message || err.message,
          type: "error",
        }),
      );
    } finally {
      setProgress(null);
      if (onDone) onDone();
    }
  };

  const running = !!progress;

  return (
    <div className="d-flex align-items-center gap-2">
      <button
        type="button"
        className="btn btn-sm btn-info text-nowrap"
        onClick={run}
        disabled={running}
        title="Copy the tracking number of every invoice not in Shipments yet -- one-customer invoices only (PK-UPS / PK-DHL, linked to the customer's work order)"
      >
        <i className={`mdi ${running ? "mdi-loading mdi-spin" : "mdi-truck-plus-outline"}`}></i>{" "}
        {running ? `Copying ${progress.done} / ${progress.total || "…"}` : "Copy to Shipments"}
      </button>
      {running && (
        <button
          type="button"
          className="btn btn-sm btn-outline-danger"
          onClick={() => (stopRef.current = true)}
        >
          Stop
        </button>
      )}
    </div>
  );
}
